import { Stack, Card } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  axiosInstance,
  axiosInstanceForTenantAPI,
} from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';
import UserRoleList from '../../../assets/utils/userRoleList';
import CustomDepartmentForm from '../../../components/customer/settings/reportForm/customDepartment/customDepartmentForm';
import InviteUser from '../../../components/customer/settings/user/inviteUser';
import UserList from '../../../components/customer/settings/user/userList';
import PartnerInviteUser from '../../../components/partner/partnerUserInvite';
import { ROLE_VALUES } from '../../../constants/customerConstant';
import { useAuth } from '../../../contexts/auth/authContext';
import useDepartmentData from '../../../hooks/fetcher/useDepartmentData';
import useReportTypeData from '../../../hooks/fetcher/useReportTypeData';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import ErrorType from '../../../interfaces/ErrorType';
import { UserData as Data } from '../../../interfaces/UserData';

import LocalizationAlert, {
  LocalizationAlertType,
} from '~/features/language/LocalizationAlert';

const SettingsUsers = () => {
  const userType = localStorage.getItem('user_type') as string;
  const userTypeAsNumber = parseInt(userType, 10);

  const { slug } = useAuth();
  const intl = useIntl();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [listOfUsers, setListOfUsers] = useState<Data[]>([]);
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [errorState, setErrorState] = useState<ErrorType>({
    errorType: false,
    message: '',
  });
  const [customDepartmentToAdd, setCustomDepartmentToAdd] = useState('');
  const { departmentList, fetchDepartmentList } = useDepartmentData();
  const { reportTypeList } = useReportTypeData();

  const GENERIC_ERROR_MESSAGE = intl.formatMessage({
    defaultMessage: 'Error happened. We\'re currently fixing it.',
  });

  const { createToastNotification } = useToastNotificationContext();

  const isAutoAssignedDepartment = (departmentIDForDelete: number) => {
    return reportTypeList.some((report) => {
      if (report.is_auto_assignment_enabled) {
        return departmentIDForDelete === report.auto_assignment_department;
      }
      return false;
    });
  };

  const departmentToAddForUpdateHandler = async () => {
    try {
      const body = {
        name: customDepartmentToAdd,
      };
      await axiosInstanceForTenantAPI(companySlug).post('/department', body);
      createToastNotification(
        intl.formatMessage({ defaultMessage: 'Department added' }),
        'success',
      );
      fetchDepartmentList();
      setCustomDepartmentToAdd('');
    } catch (err: unknown) {
      let errorMessage = GENERIC_ERROR_MESSAGE;
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = GENERIC_ERROR_MESSAGE;
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Failure to process request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You are unauthorized to get company departments',
          });
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You cannot use this request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      createToastNotification(errorMessage, 'error');
    }
  };

  const departmentHandler = async (input: number | null, action: string) => {
    if (input !== null) {
      if (action !== 'Remove') {
        try {
          const data = { is_active: action === 'Turn On' ? true : false };
          await axiosInstanceForTenantAPI().patch('/department/' + input, data);

          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Department updated' }),
            'success',
          );
          fetchDepartmentList();
        } catch (err: unknown) {
          let errorMessage = GENERIC_ERROR_MESSAGE;
          if (isRESTAPIError(err)) {
            if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
              errorMessage = GENERIC_ERROR_MESSAGE;
            } else if (
              `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage: 'Failure to process request',
              });
            } else if (
              `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage:
                  'You are unauthorized to get company departments',
              });
            } else if (
              `${err.response.status}` === String(StatusCodes.FORBIDDEN)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage: 'You cannot use this request',
              });
            } else if (
              `${err.response.status}` ===
              String(StatusCodes.INTERNAL_SERVER_ERROR)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage: 'Error happened in the server',
              });
            }
          }
          createToastNotification(errorMessage, 'error');
        }
      } else {
        try {
          if (isAutoAssignedDepartment(input) === true) {
            createToastNotification(
              intl.formatMessage({
                defaultMessage:
                  'A report type has an assigned deleted department.',
              }),
              'error',
            );
            return;
          }

          await axiosInstanceForTenantAPI().delete('/department/' + input);

          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Department removed' }),
            'success',
          );
          fetchDepartmentList();
        } catch (err: unknown) {
          let errorMessage = GENERIC_ERROR_MESSAGE;
          if (isRESTAPIError(err)) {
            if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
              errorMessage = GENERIC_ERROR_MESSAGE;
            } else if (
              `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage: 'Failure to process request',
              });
            } else if (
              `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage:
                  'You are unauthorized to get company departments',
              });
            } else if (
              `${err.response.status}` === String(StatusCodes.FORBIDDEN)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage: 'You cannot use this request',
              });
            } else if (
              `${err.response.status}` ===
              String(StatusCodes.INTERNAL_SERVER_ERROR)
            ) {
              errorMessage = intl.formatMessage({
                defaultMessage: 'Error happened in the server',
              });
            }
          }
          createToastNotification(errorMessage, 'error');
        }
      }
    }
  };

  const departmentToAddHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCustomDepartmentToAdd(event.target.value);
  };

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const fetchUserData = useCallback(async () => {
    try {
      const listOfUsersResponse = await axiosInstanceForTenantAPI(
        localStorage.getItem('slug'),
      ).get('/users');

      const listOfUsersArray =
        listOfUsersResponse.data.account_settings_user_list;

      listOfUsersArray.forEach((user: Data) => {
        if (user.user_settings_invite_status === 'Expired') {
          user.is_active = true;
        }
      });

      const filteredData = listOfUsersArray.filter(
        (user: Data) =>
          user.is_active === true ||
          (user.is_active === false &&
            user.user_settings_invite_status !== 'Accepted'),
      );

      setListOfUsers(filteredData);

      const userRoleValueResponse = await axiosInstance().get('/user-types');
      setAllRoles(userRoleValueResponse.data);

      if (!Number.isNaN(userTypeAsNumber)) {
        const userRoleList = await UserRoleList(userTypeAsNumber);
        setRoles(userRoleList);
      }
    } catch (err) {
      if (isRESTAPIError(err)) {
        setErrorState({
          errorType: true,
          message: err.message,
        });
      } else {
        setErrorState({
          errorType: false,
          message: intl.formatMessage({
            defaultMessage: 'An unexpected error occurred.',
          }),
        });
      }
    } finally {
      setFetchLoading(false);
    }
  }, [userTypeAsNumber, intl]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return (
    <Stack gap={2}>
      {userType === `${ROLE_VALUES.PARTNER_USER}` ||
      userType === `${ROLE_VALUES.PARTNER_ADMIN}` ? (
        <PartnerInviteUser
          departments={departmentList}
          fetchLoading={fetchLoading}
          setFetchLoading={setFetchLoading}
          inviteLoading={inviteLoading}
          setInviteLoading={setInviteLoading}
          roles={roles}
          refetch={fetchUserData}
        />
      ) : (
        <InviteUser
          departments={departmentList}
          fetchLoading={fetchLoading}
          setFetchLoading={setFetchLoading}
          inviteLoading={inviteLoading}
          setInviteLoading={setInviteLoading}
          roles={roles}
          refetch={fetchUserData}
        />
      )}
      <UserList
        setData={setListOfUsers}
        data={listOfUsers}
        errorState={errorState}
        fetchLoading={fetchLoading}
        roles={roles}
        allRoles={allRoles}
        departments={departmentList}
      />
      <Card
        sx={{
          borderRadius: '6px',
          border: '1px solid #00000029',
          padding: 2.5,
          boxShadow: '0px 0px 0px 0px #0000000A',
          marginBottom: '20px',
        }}
      >
        <CustomDepartmentForm
          onChangeDepartment={departmentHandler}
          list={departmentList}
          customDepartmentToAdd={customDepartmentToAdd}
          onChangeCustomDepartmentToAdd={departmentToAddHandler}
          onAddCustomDepartment={departmentToAddForUpdateHandler}
        />
      </Card>
      <LocalizationAlert
        type={LocalizationAlertType.SETTINGS_USER}
        message={intl.formatMessage({
          defaultMessage:
            'To view the translated version of added departments, proceed to language settings on the sidebar and go to multi-language support',
        })}
      />
      <div className='h-10' />
    </Stack>
  );
};

export default SettingsUsers;
